// biome-ignore-all lint/suspicious/noExplicitAny: not changing all these now
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityLogDto,
  FeedbackFormFilterType,
  FeedbackFormQueryFilterSchemaDto,
  GetAllParamsDto,
  PriorityFrameworkDto,
} from '@tempo-io/fim3-dto';
import {
  type CreateFeedbackFormDto,
  type DuplicateFeedbackFormDto,
  type FeedbackFormDto,
  type FeedbackFormProductsDto,
  type FeedbackFormWithFieldsDto,
  type GetFeedbackFormsResponseDto,
  LifecycleFilter,
  type UpdateFeedbackFormDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';

export const useFeedbackFormsApi = (api: AxiosInstance) => {
  const baseUrl = `/feedback-forms`;
  const defaultLimit = 25;

  const createFeedbackForm = async (
    dto: CreateFeedbackFormDto,
  ): Promise<{ data: FeedbackFormDto }> => {
    const { data } = await api.post(baseUrl, dto);
    return data;
  };

  const getFeedbackForms = async (
    params: GetAllParamsDto & {
      stateFilter?: FeedbackFormFilterType;
      priorityFrameworkId?: number;
      portalId?: number;
    },
  ): Promise<GetFeedbackFormsResponseDto> => {
    const { data } = await api.get(`${baseUrl}`, { params });
    return data;
  };

  const getFeedbackForm = async (id: number): Promise<{ data: FeedbackFormWithFieldsDto }> => {
    const { data } = await api.get(`${baseUrl}/${id}`).catch((error) => {
      if (error.status === 404) {
        return { data: null };
      }
      throw error;
    });
    return data;
  };

  const getFeedbackFormBySlug = async (slug: string): Promise<{ data: FeedbackFormDto }> => {
    const { data } = await api.get(`${baseUrl}/config/${slug}`);
    return data;
  };

  const updateFeedbackForm = async (
    updatedFeedbackForm: UpdateFeedbackFormDto,
  ): Promise<{ data: FeedbackFormDto }> => {
    const { data } = await api.put(`${baseUrl}/${updatedFeedbackForm.id}`, {
      ...updatedFeedbackForm,
      id: undefined,
    });
    return data;
  };

  const addFramework = async ({
    id,
    frameworkId,
  }: {
    id: number;
    frameworkId: number;
  }): Promise<{ data: PriorityFrameworkDto }> => {
    const { data } = await api.put(`${baseUrl}/${id}/framework/${frameworkId}`);
    return data;
  };

  const toggleFeedbackFormActive = async (productAreaDetails: {
    id: number;
    isActive: boolean;
  }): Promise<{ data: FeedbackFormDto }> => {
    const { id, isActive } = productAreaDetails;
    const data = await updateFeedbackForm({ id, isActive });
    return data;
  };

  const archiveFeedbackForm = async (id: number) => {
    const { data } = await api.put(`${baseUrl}/${id}/archive`);
    return data;
  };

  const restoreFeedbackForm = async ({
    id,
    dto,
  }: {
    id: number;
    dto?: { portalId: number | null };
  }) => {
    const { data } = await api.put(`${baseUrl}/${id}/restore`, dto);
    return data;
  };

  const deleteFeedbackForm = async (id: number) => {
    const { data } = await api.delete(`${baseUrl}/${id}`);
    return data;
  };

  const duplicateFeedbackForm = async ({
    id,
    dto,
  }: {
    id: number;
    dto: DuplicateFeedbackFormDto;
  }): Promise<{ data: FeedbackFormDto }> => {
    const { data } = await api.post(`${baseUrl}/${id}/duplicate`, dto);
    return data;
  };

  const moveFeedbackFormToPortal = async ({
    id,
    dto,
  }: {
    id: number;
    dto: { portalId: number | null };
  }): Promise<{ data: FeedbackFormDto }> => {
    const { data } = await api.put(`${baseUrl}/${id}/portal`, dto);
    return data;
  };

  const getFeedbackFormProducts = async (
    id?: number,
    activeOnly?: boolean,
  ): Promise<{ data: FeedbackFormProductsDto | undefined }> => {
    if (id === undefined || id === -1) return { data: undefined };

    const { data } = await api.get(`${baseUrl}/${id}/products`, { params: { activeOnly } });
    return data;
  };

  const getFeedbackActivityLogs = async (id: number) => {
    const { data } = await api.get(`${baseUrl}/${id}/activity-logs`);

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(api, userIds, data, (item: any) => item.userId);

    return result;
  };

  const getPublishedDefaultPortalFormsCount = async (): Promise<{ count: number }> => {
    const { data } = await api.get(`${baseUrl}/default-portal/published-count`);
    return data;
  };

  const queryClient = useQueryClient();

  const useGetAll = (
    params: GetAllParamsDto & {
      stateFilter?: FeedbackFormFilterType;
      priorityFrameworkId?: number;
      portalId?: number;
      filters?: FeedbackFormQueryFilterSchemaDto;
    },
    options?: { enabled?: boolean },
  ) => {
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [baseUrl, 'GetAll', params?.lifecycleFilter, params],
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit, filters: params.filters };
        return getFeedbackForms(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
      enabled: options?.enabled ?? true,
      placeholderData: (previousData) => previousData,
    });
  };

  const useGetOne = (id: number) =>
    useQuery({ queryKey: [baseUrl, id], queryFn: () => getFeedbackForm(id) });
  const useGetOneBySlug = (slug: string) =>
    useQuery({
      queryKey: [baseUrl, slug],
      queryFn: () => getFeedbackFormBySlug(slug),
    });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createFeedbackForm,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });
  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: updateFeedbackForm,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: [baseUrl, data.data.id] });
      },
    });

  const useAddFramework = () =>
    useErrorHandlingMutation({
      mutationFn: addFramework,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: [baseUrl, data.data.id] });
      },
    });

  const useToggleActive = () =>
    useErrorHandlingMutation({
      mutationFn: toggleFeedbackFormActive,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });

  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archiveFeedbackForm,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'GetAll', LifecycleFilter.Archived],
          }),
        ]),
    });

  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restoreFeedbackForm,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'GetAll', LifecycleFilter.Active],
          }),
        ]),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteFeedbackForm,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });

  const useDuplicate = () =>
    useErrorHandlingMutation({
      mutationFn: duplicateFeedbackForm,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });

  const useMoveToPortal = () =>
    useErrorHandlingMutation({
      mutationFn: moveFeedbackFormToPortal,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });

  const useFeedbackFormProducts = (id?: number, activeOnly?: boolean) => {
    return useQuery({
      queryKey: [baseUrl, id, 'products', activeOnly],
      queryFn: () => getFeedbackFormProducts(id, activeOnly),
    });
  };

  const useGetActivityLog = (id: number) =>
    useQuery({
      queryKey: [baseUrl, id, 'activity-logs'],
      queryFn: () => getFeedbackActivityLogs(id),
    });

  const usePublishedDefaultPortalFormsCount = () =>
    useQuery({
      queryKey: [baseUrl, 'default-portal', 'published-count'],
      queryFn: () => getPublishedDefaultPortalFormsCount(),
    });

  return {
    useCreate,
    useUpdate,
    useAddFramework,
    useToggleActive,
    useGetOne,
    useGetAll,
    useArchive,
    useRestore,
    useDelete,
    useDuplicate,
    useMoveToPortal,
    useGetOneBySlug,
    useFeedbackFormProducts,
    useGetActivityLog,
    usePublishedDefaultPortalFormsCount,
    getFeedbackForms,
  };
};
